/**
 * PrinterUnavailableModal — Modal global «Sin impresora conectada» (compuerta de impresión).
 *
 * QUÉ HACE:
 *  - Montado en MainLayout: cuando cualquier flujo intenta imprimir sin
 *    impresora disponible, `printerGateStore` crea una solicitud pendiente y
 *    este modal bloquea la acción final hasta que el usuario decide.
 *  - Mensaje claro («no se podrá imprimir en térmica») + confirmación
 *    «¿Seguro que quieres imprimir?» + casilla «No volver a mostrar».
 *  - Botón «Configurar impresora»: abre el diálogo de configuración POS DE
 *    CAJA (`PosHardwareSettingsDialog`, mode full) — global, sin importar
 *    permisos: solo se necesita configurar e imprimir.
 *
 * QUÉ INVOCA: usePrinterGateStore, PosHardwareSettingsDialog, Modal (ui).
 * QUÉ EXPORTA: PrinterUnavailableModal (named).
 *
 * QUÉ NO DEBE HACER:
 *  - No imprime ni decide fallbacks (eso vive en posHardware.ts / callers).
 *  - No usa permisos: el acceso a configuración POS desde aquí es abierto.
 */
import { useState } from "react";
import { Printer, Settings } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { usePrinterGateStore } from "@/store/printerGateStore";
import { PosHardwareSettingsDialog } from "./PosHardwareSettingsModal";

export function PrinterUnavailableModal() {
  const request = usePrinterGateStore((s) => s.request);
  const resolve = usePrinterGateStore((s) => s.resolve);
  const suppressForever = usePrinterGateStore((s) => s.suppressForever);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const close = (proceed: boolean) => {
    if (dontShowAgain) suppressForever();
    setDontShowAgain(false);
    resolve(proceed);
  };

  return (
    <>
      <Modal
        isOpen={request !== null}
        onClose={() => close(false)}
        title="Sin impresora conectada"
        subtitle="No se pudo imprimir"
        size="sm"
        preventOverlayClose
        preventEscapeClose
        showCloseButton={false}
        icon={<Printer size={18} className="text-primary-600" />}
        footer={
          <>
            <button
              type="button"
              onClick={() => close(false)}
              className="flex-1 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-xs sm:text-sm font-semibold text-gray-700 dark:text-slate-200 transition-colors hover:bg-gray-50 dark:hover:bg-slate-700"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => close(true)}
              className="flex-1 rounded-lg bg-primary-600 px-3 py-2 text-xs sm:text-sm font-bold text-white transition-colors hover:bg-primary-700"
            >
              {request?.context === "venta" ? "Vender igual" : "Sí, imprimir"}
            </button>
          </>
        }
      >
        <div className="space-y-3.5">
          <p className="text-[13px] sm:text-sm leading-snug text-gray-700 dark:text-slate-200">
            No tienes impresora conectada — {request?.tipo} no se podrá
            imprimir.
            {request?.context === "venta"
              ? " ¿Seguro que quieres vender sin recibo de todas formas?"
              : " ¿Seguro que quieres imprimir de todas formas?"}
          </p>

          <button
            type="button"
            onClick={() => {
              if (dontShowAgain) suppressForever();
              setShowSettings(true);
            }}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-950/40 px-3 py-2 text-xs sm:text-sm font-bold text-primary-700 dark:text-primary-300 transition-colors hover:bg-primary-100 dark:hover:bg-primary-900/50"
          >
            <Settings size={15} />
            Configurar impresora
          </button>

          <label className="flex items-start gap-2 text-[11px] sm:text-xs text-gray-600 dark:text-slate-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            No volver a mostrar este aviso
          </label>
        </div>
      </Modal>

      {/* Configuración POS de caja: global, sin importar permisos. */}
      <PosHardwareSettingsDialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        mode="full"
        canRunHardwareTests
      />
    </>
  );
}
